/**
 * Mad Dad Labz Browser Compatibility Detection
 * Detects browser capabilities and applies appropriate fallbacks
 * Ensures consistent experience across 95%+ browser market share
 */

class MadDadBrowserCompatibility {
  constructor() {
    this.features = {};
    this.browserInfo = {};
    this.init();
  }

  init() {
    this.detectBrowser();
    this.detectFeatures();
    this.applyFallbacks();
    this.addBodyClasses();
  }

  /**
   * Detect browser type and version
   */
  detectBrowser() {
    const ua = navigator.userAgent;
    
    // Chrome
    if (ua.includes('Chrome') && !ua.includes('Edge')) {
      this.browserInfo.name = 'chrome';
      this.browserInfo.version = this.extractVersion(ua, /Chrome\/(\d+)/);
    }
    // Firefox
    else if (ua.includes('Firefox')) {
      this.browserInfo.name = 'firefox';
      this.browserInfo.version = this.extractVersion(ua, /Firefox\/(\d+)/);
    }
    // Safari
    else if (ua.includes('Safari') && !ua.includes('Chrome')) {
      this.browserInfo.name = 'safari';
      this.browserInfo.version = this.extractVersion(ua, /Version\/(\d+)/);
    }
    // Edge
    else if (ua.includes('Edge') || ua.includes('Edg/')) {
      this.browserInfo.name = 'edge';
      this.browserInfo.version = this.extractVersion(ua, /(?:Edge|Edg)\/(\d+)/);
    }
    // Internet Explorer
    else if (ua.includes('Trident') || ua.includes('MSIE')) {
      this.browserInfo.name = 'ie';
      this.browserInfo.version = this.extractVersion(ua, /(?:MSIE |rv:)(\d+)/);
    }
    else {
      this.browserInfo.name = 'unknown';
      this.browserInfo.version = 0;
    }

    // Mobile detection
    this.browserInfo.isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
    this.browserInfo.isTablet = /iPad|Android(?!.*Mobile)/i.test(ua);
  }

  extractVersion(ua, regex) {
    const match = ua.match(regex);
    return match ? parseInt(match[1], 10) : 0;
  }

  /**
   * Detect CSS and JavaScript feature support
   */
  detectFeatures() {
    // CSS Grid support
    this.features.cssGrid = CSS.supports('display', 'grid');
    
    // CSS Custom Properties support
    this.features.cssCustomProperties = CSS.supports('--test', 'value');
    
    // CSS Flexbox support
    this.features.flexbox = CSS.supports('display', 'flex');
    
    // Object-fit support
    this.features.objectFit = CSS.supports('object-fit', 'cover');
    
    // CSS Transforms support
    this.features.transforms = CSS.supports('transform', 'translateX(0)');
    
    // CSS Animations support
    this.features.animations = CSS.supports('animation', 'fadeIn 1s ease');
    
    // CSS Filters support
    this.features.filters = CSS.supports('filter', 'blur(5px)');
    
    // Intersection Observer support
    this.features.intersectionObserver = 'IntersectionObserver' in window;
    
    // WebP support detection
    this.detectWebPSupport();
    
    // Touch support
    this.features.touch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    
    // Reduced motion preference
    this.features.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    // High contrast preference
    this.features.highContrast = window.matchMedia('(prefers-contrast: high)').matches;
    
    // JavaScript enabled (obviously true if this runs)
    this.features.javascript = true;
    
    // Font loading API support
    this.features.fontLoading = 'fonts' in document;
    
    // Service Worker support
    this.features.serviceWorker = 'serviceWorker' in navigator;
    
    // Local Storage support
    this.features.localStorage = this.testLocalStorage();
    
    // Session Storage support
    this.features.sessionStorage = this.testSessionStorage();
  }

  detectWebPSupport() {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    this.features.webp = canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
  }

  testLocalStorage() {
    try {
      const test = 'test';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  testSessionStorage() {
    try {
      const test = 'test';
      sessionStorage.setItem(test, test);
      sessionStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * Apply appropriate fallbacks based on detected features
   */
  applyFallbacks() {
    // CSS Grid fallbacks
    if (!this.features.cssGrid) {
      this.applyCSSGridFallbacks();
    }

    // Intersection Observer fallbacks
    if (!this.features.intersectionObserver) {
      this.applyIntersectionObserverFallbacks();
    }

    // WebP fallbacks
    if (!this.features.webp) {
      this.applyWebPFallbacks();
    }

    // Object-fit fallbacks
    if (!this.features.objectFit) {
      this.applyObjectFitFallbacks();
    }

    // Animation fallbacks for reduced motion
    if (this.features.reducedMotion) {
      this.applyReducedMotionFallbacks();
    }

    // Touch device optimizations
    if (this.features.touch) {
      this.applyTouchOptimizations();
    }

    // High contrast mode adjustments
    if (this.features.highContrast) {
      this.applyHighContrastMode();
    }

    // Browser-specific fixes
    this.applyBrowserSpecificFixes();
  }

  applyCSSGridFallbacks() {
    const gridElements = document.querySelectorAll('.mad-dad-chaos-grid');
    gridElements.forEach(grid => {
      grid.style.display = 'flex';
      grid.style.flexWrap = 'wrap';
      grid.style.justifyContent = 'space-between';
      
      const items = grid.querySelectorAll('.card-wrapper');
      items.forEach(item => {
        item.style.width = 'calc(50% - 8px)';
        item.style.marginBottom = '16px';
      });
    });
  }

  applyIntersectionObserverFallbacks() {
    // Immediately show all lazy-loaded content
    const lazyElements = document.querySelectorAll('.mad-dad-stagger-reveal, .mad-dad-lazy-animate');
    lazyElements.forEach(element => {
      element.style.opacity = '1';
      element.style.transform = 'none';
    });

    // Load all lazy images immediately
    const lazyImages = document.querySelectorAll('img[data-src]');
    lazyImages.forEach(img => {
      if (img.dataset.src) {
        img.src = img.dataset.src;
      }
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
      }
    });
  }

  applyWebPFallbacks() {
    const webpImages = document.querySelectorAll('[data-webp-src]');
    webpImages.forEach(img => {
      if (img.dataset.src) {
        img.src = img.dataset.src;
      }
    });

    const webpBackgrounds = document.querySelectorAll('[data-webp-bg-src]');
    webpBackgrounds.forEach(element => {
      if (element.dataset.bgSrc) {
        element.style.backgroundImage = `url(${element.dataset.bgSrc})`;
      }
    });
  }

  applyObjectFitFallbacks() {
    const objectFitImages = document.querySelectorAll('.mad-dad-product-image img');
    objectFitImages.forEach(img => {
      const container = img.parentElement;
      if (container) {
        container.style.backgroundImage = `url(${img.src})`;
        container.style.backgroundSize = 'cover';
        container.style.backgroundPosition = 'center';
        container.style.backgroundRepeat = 'no-repeat';
        img.style.opacity = '0';
      }
    });
  }

  applyReducedMotionFallbacks() {
    const animatedElements = document.querySelectorAll('.mad-dad-laboratory-icon, .mad-dad-stagger-reveal, .btn.mad-dad-primary');
    animatedElements.forEach(element => {
      element.style.animation = 'none';
      element.style.transition = 'none';
      element.style.transform = 'none';
    });
  }

  applyTouchOptimizations() {
    // Increase touch targets
    const touchTargets = document.querySelectorAll('.btn, .mad-dad-scientific-label, .mad-dad-trust-badge');
    touchTargets.forEach(target => {
      const currentHeight = parseInt(getComputedStyle(target).minHeight) || 0;
      if (currentHeight < 44) {
        target.style.minHeight = '44px';
        target.style.minWidth = '44px';
      }
    });

    // Disable hover effects on touch devices
    const hoverElements = document.querySelectorAll('.mad-dad-hover-effect');
    hoverElements.forEach(element => {
      element.addEventListener('touchstart', () => {
        element.classList.add('mad-dad-touch-active');
      });
    });
  }

  applyHighContrastMode() {
    document.documentElement.style.setProperty('--mad-dad-primary', '#000000');
    document.documentElement.style.setProperty('--mad-dad-calm', '#ffffff');
    document.documentElement.style.setProperty('--mad-dad-energy', '#0066cc');
  }

  applyBrowserSpecificFixes() {
    switch (this.browserInfo.name) {
      case 'ie':
        this.applyIEFixes();
        break;
      case 'safari':
        this.applySafariFixes();
        break;
      case 'firefox':
        this.applyFirefoxFixes();
        break;
      case 'edge':
        if (this.browserInfo.version < 79) {
          this.applyEdgeLegacyFixes();
        }
        break;
    }
  }

  applyIEFixes() {
    // IE11 specific fixes
    const gridElements = document.querySelectorAll('.mad-dad-chaos-grid');
    gridElements.forEach(grid => {
      grid.style.display = '-ms-flexbox';
      grid.style.display = 'flex';
      grid.style.msFlexWrap = 'wrap';
      grid.style.flexWrap = 'wrap';
    });

    // IE11 CSS custom properties polyfill
    if (!this.features.cssCustomProperties) {
      this.loadIECustomPropertiesPolyfill();
    }
  }

  applySafariFixes() {
    // Safari-specific input styling
    const inputs = document.querySelectorAll('input, button');
    inputs.forEach(input => {
      input.style.webkitAppearance = 'none';
      input.style.borderRadius = '0';
    });

    // Safari viewport height fix
    this.fixSafariViewportHeight();
  }

  applyFirefoxFixes() {
    // Firefox image rendering
    const images = document.querySelectorAll('.mad-dad-laboratory-icon img');
    images.forEach(img => {
      img.style.imageRendering = '-moz-crisp-edges';
    });
  }

  applyEdgeLegacyFixes() {
    // Edge Legacy grid fixes
    const gridElements = document.querySelectorAll('.mad-dad-chaos-grid');
    gridElements.forEach(grid => {
      grid.style.display = '-ms-grid';
      grid.style.msGridColumns = '1fr 1fr';
    });
  }

  fixSafariViewportHeight() {
    // Fix for Safari's viewport height issues
    const setVH = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVH();
    window.addEventListener('resize', setVH);
    window.addEventListener('orientationchange', setVH);
  }

  loadIECustomPropertiesPolyfill() {
    // Load CSS custom properties polyfill for IE11
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/css-vars-ponyfill@2.4.8/dist/css-vars-ponyfill.min.js';
    script.onload = () => {
      if (window.cssVars) {
        window.cssVars({
          include: 'style,link[rel="stylesheet"]',
          onlyLegacy: true
        });
      }
    };
    document.head.appendChild(script);
  }

  /**
   * Add browser and feature classes to body
   */
  addBodyClasses() {
    if (!document.body) {
      document.addEventListener('DOMContentLoaded', () => this.addBodyClasses(), { once: true });
      return;
    }

    const classes = [];

    // Browser classes
    classes.push(`browser-${this.browserInfo.name}`);
    classes.push(`browser-${this.browserInfo.name}-${this.browserInfo.version}`);

    // Device classes
    if (this.browserInfo.isMobile) classes.push('device-mobile');
    if (this.browserInfo.isTablet) classes.push('device-tablet');
    if (!this.browserInfo.isMobile && !this.browserInfo.isTablet) classes.push('device-desktop');

    // Feature classes
    Object.keys(this.features).forEach(feature => {
      const className = this.features[feature] ? feature : `no-${feature}`;
      classes.push(className);
    });

    // Add all classes to body
    document.body.classList.add(...classes);
  }

  /**
   * Public API for feature detection
   */
  hasFeature(feature) {
    return this.features[feature] || false;
  }

  getBrowserInfo() {
    return { ...this.browserInfo };
  }

  getFeatures() {
    return { ...this.features };
  }

  /**
   * Test browser compatibility and report issues
   */
  runCompatibilityTest() {
    const issues = [];
    const warnings = [];

    // Check for critical feature support
    if (!this.features.javascript) {
      issues.push('JavaScript is disabled');
    }

    if (!this.features.flexbox) {
      issues.push('CSS Flexbox not supported');
    }

    if (!this.features.localStorage) {
      warnings.push('Local Storage not available');
    }

    if (!this.features.cssGrid) {
      warnings.push('CSS Grid not supported - using flexbox fallback');
    }

    if (!this.features.intersectionObserver) {
      warnings.push('Intersection Observer not supported - using fallback loading');
    }

    // Browser version checks
    const minVersions = {
      chrome: 70,
      firefox: 65,
      safari: 12,
      edge: 79
    };

    const minVersion = minVersions[this.browserInfo.name];
    if (minVersion && this.browserInfo.version < minVersion) {
      warnings.push(`${this.browserInfo.name} ${this.browserInfo.version} is below recommended version ${minVersion}`);
    }

    return {
      compatible: issues.length === 0,
      issues,
      warnings,
      browserInfo: this.browserInfo,
      features: this.features
    };
  }
}

// Initialize browser compatibility detection when DOM is ready and safe
let madDadBrowserCompatibility;
let madDadBrowserCompatibilityInitScheduled = false;

function initializeMadDadBrowserCompatibility() {
  if (madDadBrowserCompatibility) {
    return madDadBrowserCompatibility;
  }

  if (!document?.body || !document.body.classList) {
    if (!madDadBrowserCompatibilityInitScheduled) {
      madDadBrowserCompatibilityInitScheduled = true;
      requestAnimationFrame(() => {
        madDadBrowserCompatibilityInitScheduled = false;
        initializeMadDadBrowserCompatibility();
      });
    }
    return null;
  }

  try {
    madDadBrowserCompatibility = new MadDadBrowserCompatibility();

    if (window.Shopify?.designMode || localStorage.getItem('mad-dad-debug') === 'true') {
      const compatibilityReport = madDadBrowserCompatibility.runCompatibilityTest();
      console.log('Mad Dad Labz Browser Compatibility Report:', compatibilityReport);
    }

    window.madDadBrowserCompatibility = madDadBrowserCompatibility;
  } catch (error) {
    console.error('Mad Dad Labz Browser Compatibility failed to initialize', error);
  }

  return madDadBrowserCompatibility;
}

const scheduleMadDadBrowserCompatibilityInit = () => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeMadDadBrowserCompatibility(), { once: true });
  } else {
    initializeMadDadBrowserCompatibility();
  }
};

scheduleMadDadBrowserCompatibilityInit();

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = MadDadBrowserCompatibility;
}